import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { captureReferralFromUrl } from "./lib/referral";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import LeadCaptureModal from "./components/LeadCaptureModal";
import PublicChatWidget from "./components/PublicChatWidget";
import Home from "./pages/Home";
import Services from "./pages/Services";
import About from "./pages/About";
import Booking from "./pages/Booking";
import Contact from "./pages/Contact";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Ebook from "./pages/Ebook";
import EbookSuccess from "./pages/EbookSuccess";
import Admin from "./pages/Admin";
import Tools from "./pages/Tools";
import Marketplace from "./pages/Marketplace";
import SignContract from "./pages/SignContract";
import CreditReport from "./pages/CreditReport";
import Insights from "./pages/Insights";

function Protected({ children, requireAdmin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="section text-center text-sm text-[#1B211A]/60">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireAdmin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function ShellRoutes() {
  const location = useLocation();
  // CRITICAL: Detect Google OAuth callback synchronously, before normal routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/services" element={<Services />} />
      <Route path="/about" element={<About />} />
      <Route path="/booking" element={<Booking />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/tools" element={<Tools />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/sign/:requestId" element={<SignContract />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/ebook" element={<Ebook />} />
      <Route path="/ebook/success" element={<EbookSuccess />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/insights" element={<Protected><Insights /></Protected>} />
      <Route path="/admin/clients/:userId/insights" element={<Protected requireAdmin><Insights /></Protected>} />
      <Route path="/report/:documentId" element={<Protected><CreditReport /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected requireAdmin><Admin /></Protected>} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Shell() {
  useEffect(() => {
    captureReferralFromUrl();
  }, []);
  return (
    <div className="grain min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <ShellRoutes />
      </main>
      <Footer />
      <LeadCaptureModal />
      <PublicChatWidget />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}
